import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { IAllGroupModel } from 'domains/designers/RuleDesigner/models/AllGroupModel';
import {
  ValueCell,
  OutputCells,
  Cell,
  ActionsCell,
  SettingsCell,
  EvaluationCell,
  HeaderActionValueCell,
  PlaceholderCell,
} from '../cells';
import { RuleOutputPath } from '@decisions/server/DecisionsFramework/Design/Flow';
import { ShowContextEditorLayerCallback } from 'domains/designers/TruthTable/TruthTable';
import {
  HandleNotificationsCallback,
  getRowExecutionResult,
  getHeaders,
  rowOutputSetValue,
  addRowBefore,
  addRowAfter,
  getHeaderActions,
  hasActionsColumn,
  getTableColumnsCount,
  getPhraseColumnName,
  getStepsForHeader,
} from '../TruthTableUtils';
import {
  getEditContextForTruthTableRowResult,
  getEditContextForTruthTableSpecifiedResult,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/RuleEditService';
import {
  getRowSettings,
  startEditingContextForTruthTableRowResult,
  startEditingForTruthTableSpecificResult,
  setDefaultVerbForCell,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/TruthTable/TruthTableEditService';
import './TruthTableRow.less';
import { AddPhraseColumn, OrderNumberColumn } from '../columns';
import { RuleDesignerContext } from 'domains/designers/RuleDesigner/RuleDesignerContext';
import { useVisibleRow } from '../hooks/useVisibleRow';
import { PhraseItemClickEvent } from '../header-cells/HeaderPhraseCell';
import { IRuleStepModel } from 'domains/designers/RuleDesigner/models/RuleStepModel';
import { IRootGroupModel } from 'domains/designers/RuleDesigner/models/RootGroupModel';

interface ITruthTableRowProps extends React.HTMLProps<HTMLTableRowElement> {
  rootModel: IRootGroupModel;
  rowModel: IAllGroupModel;
  handleNotifications: HandleNotificationsCallback;
  showContextEditorLayer: ShowContextEditorLayerCallback;
  onVerbClick: (event: PhraseItemClickEvent<IRuleStepModel>) => void;
}

const getClassName = (className?: string) => classNames('tt-table__row', className);

/**
 * Represents regular truth table row.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TruthTableRow({
  rootModel,
  rowModel,
  className,
  showContextEditorLayer,
  handleNotifications,
  onVerbClick,
  ...otherProps
}: ITruthTableRowProps) {
  const headers = getHeaders(rootModel);
  const { isDebug, isSampling, designerSurface, isLargeRule } = useContext(RuleDesignerContext);
  const executionResult = getRowExecutionResult(rowModel, isDebug, isSampling);
  const sessionId = rootModel.RuleSessionId;
  const headerActions = getHeaderActions(rowModel);
  const { rowRef, isVisible } = useVisibleRow(!isLargeRule);

  const colSpan = getTableColumnsCount(rootModel, designerSurface);

  const handleUseDefaultVerbClick = useCallback(
    (step: IRuleStepModel) => {
      setDefaultVerbForCell({
        ruleSessionId: sessionId,
        stepId: step.Id,
      });
    },
    [sessionId]
  );

  return (
    <tr ref={rowRef} className={getClassName(className)} {...otherProps}>
      {!isVisible && <PlaceholderCell colSpan={colSpan} />}
      {isVisible && (
        <>
          <OrderNumberColumn>
            <Cell>{rowModel.DisplayData.ExecutionNumber}</Cell>
          </OrderNumberColumn>
          {headers.map((header, index) => {
            const step = getStepsForHeader(rowModel, header)[0];

            return (
              <ValueCell
                key={`${rootModel.Id}-${index}`}
                index={index}
                rootModel={rootModel}
                ruleStep={step}
                showContextEditorLayer={showContextEditorLayer}
                onVerbClick={onVerbClick}
                onUseDefaultVerbClick={handleUseDefaultVerbClick}
                data-column-name={getPhraseColumnName(header)}
              />
            );
          })}
          {!isDebug && <AddPhraseColumn />}
          <OutputCells
            executionResult={executionResult}
            rowModel={rowModel}
            ruleType={rootModel.RuleDisplayData.RuleType}
            outputs={rowModel.DisplayData.StepResults}
            showContextEditorLayer={showContextEditorLayer}
            setInlineValue={rowOutputSetValue}
            getSingleResultContext={({ ruleSessionId }) =>
              getEditContextForTruthTableRowResult({
                ruleSessionId: ruleSessionId,
                ruleStepId: rowModel.Id,
              })
            }
            getSingleResultInputMappingSession={({ ruleSessionId }) =>
              startEditingContextForTruthTableRowResult({
                stepId: rowModel.Id,
                truthTableSessionId: ruleSessionId,
              })
            }
            getMultipleResultContext={({ ruleStepId, ruleSessionId, resultName }) =>
              getEditContextForTruthTableSpecifiedResult({
                ruleSessionId,
                ruleStepId,
                resultName,
              })
            }
            getMultipleResultInputMappingSession={({ ruleSessionId, ruleStepId, resultName }) =>
              startEditingForTruthTableSpecificResult({
                truthTableSessionId: ruleSessionId,
                stepId: ruleStepId,
                resultName: resultName,
              })
            }
          />
          {hasActionsColumn(rootModel, designerSurface) && (
            <ActionsCell
              actionsModel={rowModel.ActionsModel}
              outputPath={RuleOutputPath.True}
              executionResult={executionResult}
            />
          )}
          {headerActions?.map(item => (
            <HeaderActionValueCell
              key={item.ActionId}
              sessionId={item.getSessionId()}
              actionModel={item}
              executionResult={executionResult}
            />
          ))}
          {!isDebug && (
            <SettingsCell
              rowId={rowModel.Id}
              ruleSessionId={sessionId}
              handleNotifications={handleNotifications}
              showContextEditorLayer={showContextEditorLayer}
              onAddRowBefore={() => {
                addRowBefore(sessionId, rowModel.Id, handleNotifications);
              }}
              onAddRowAfter={() => {
                addRowAfter(sessionId, rowModel.Id, handleNotifications);
              }}
              getRowSettings={rowId => {
                return getRowSettings({
                  ruleSessionId: sessionId,
                  rowId,
                });
              }}
            />
          )}
          <EvaluationCell executionResult={executionResult} />
        </>
      )}
    </tr>
  );
}
